import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Socket } from 'socket.io-client'
import { createWebchatSocket } from '@/services/webchatSocket'
import { getApiBaseUrl } from '@/config/env'
import toast from 'react-hot-toast'
import {
  RotateCcw,
  CheckCircle2,
  Image as ImageIcon,
  X,
  ExternalLink,
  Bot,
  User,
  ShieldCheck,
  Send,
  Activity,
  ShoppingBag,
  Layers,
  Play,
} from 'lucide-react'
import {
  traceService,
  type TestChatEvaluationResult,
  type SendTestMessageResult,
} from '../services/traceService'
import type { Tenant } from '../components/flowDebugger/types'
import TestChatEvaluationModal from '../../components/bot/TestChatEvaluationModal'

interface ChatMessage {
  id?: string
  role: 'bot' | 'user'
  text: string
  time: string
  stage?: string
  mediaUrl?: string
  mediaMimetype?: string
}

interface TurnTraceInfo {
  turnIndex: number
  userInput: string
  botReply: string
  stage?: string
  intent?: string
  route?: string
  timestamp: string
  durationMs?: number
  selectedProduct?: any
  executionPlan?: any
  metadata?: any
}

function getTime() {
  return new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
}

function getOrCreateVisitorId(tenantId: string): string {
  const key = `saas_sim_visitor_${tenantId}`
  let id = sessionStorage.getItem(key)
  if (!id) {
    id = `sim_${tenantId.slice(0, 8)}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`
    sessionStorage.setItem(key, id)
  }
  return id
}

function getStageBadge(stage?: string) {
  if (!stage) return { label: 'Descubrimiento', icon: '🔍', color: 'bg-blue-900/40 text-blue-300 border-blue-700/60' }
  const s = stage.toUpperCase()
  if (s.includes('CLOS')) return { label: 'Cierre de Venta', icon: '🤝', color: 'bg-emerald-900/40 text-emerald-300 border-emerald-700/60 font-semibold' }
  if (s.includes('CHECK')) return { label: 'Checkout / Pago', icon: '💳', color: 'bg-purple-900/40 text-purple-300 border-purple-700/60 font-semibold' }
  if (s.includes('OBJEC')) return { label: 'Objeción', icon: '💬', color: 'bg-amber-900/40 text-amber-300 border-amber-700/60' }
  if (s.includes('RECOM') || s.includes('PROP')) return { label: 'Propuesta', icon: '🛍️', color: 'bg-indigo-900/40 text-indigo-300 border-indigo-700/60' }
  if (s.includes('QUAL')) return { label: 'Calificación', icon: '🎯', color: 'bg-cyan-900/40 text-cyan-300 border-cyan-700/60' }
  return { label: 'Descubrimiento', icon: '🔍', color: 'bg-blue-900/40 text-blue-300 border-blue-700/60' }
}

const QUICK_SCENARIOS = [
  {
    label: '🔍 Pastillas Corolla 2018',
    text: 'Hola, buenas tardes. Tienen pastillas de freno delanteras para Toyota Corolla 2018?',
    category: 'Búsqueda y Catálogo',
  },
  {
    label: '⚖️ Original vs Reemplazo',
    text: 'Cual es la diferencia entre la original y el reemplazo economico? Cual me recomiendas?',
    category: 'Dinámica 1',
  },
  {
    label: '💰 Objeción de Precio',
    text: 'La original se sale un poco de mi presupuesto. Tienen alguna opción con descuento o más barata?',
    category: 'Dinámica 2',
  },
  {
    label: '🚚 Cierre + Despacho',
    text: 'Perfecto, me llevo el repuesto original. Me lo pueden enviar hoy al taller o paso retirando?',
    category: 'Dinámica 3',
  },
  {
    label: '➕ Combo / Líquido DOT 4',
    text: 'Excelente, agregame tambien un pote de liquido de frenos DOT 4 para hacer el cambio completo.',
    category: 'Cross-selling',
  },
]

export default function DebugSimulatorPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialTenantId = searchParams.get('tenantId') || ''

  const [tenants, setTenants] = useState<Tenant[]>([])
  const [tenantId, setTenantId] = useState(initialTenantId)
  const [botName, setBotName] = useState('Asistente de Ventas')
  const [conversationId, setConversationId] = useState<string>('')
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [turnTraces, setTurnTraces] = useState<TurnTraceInfo[]>([])
  const [botTyping, setBotTyping] = useState(false)
  const [userInput, setUserInput] = useState('')
  const [socketStatus, setSocketStatus] = useState<'connecting' | 'connected' | 'offline'>('connecting')
  const [currentStage, setCurrentStage] = useState<string>('DISCOVERY')
  const [stageHistory, setStageHistory] = useState<string[]>(['DISCOVERY'])
  const [selectedProduct, setSelectedProduct] = useState<{ id?: string; name?: string; price?: number; imageUrl?: string } | null>(null)
  const [isResetting, setIsResetting] = useState(false)
  const [isEvaluating, setIsEvaluating] = useState(false)
  const [evaluationResult, setEvaluationResult] = useState<TestChatEvaluationResult | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [pendingImage, setPendingImage] = useState<{ dataUrl: string; name: string; file: File } | null>(null)
  const [zoomedImage, setZoomedImage] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'turns' | 'state' | 'raw'>('turns')
  const replayConversationId = searchParams.get('replayConversationId') || ''
  const [isReplaying, setIsReplaying] = useState(false)

  const chatEndRef = useRef<HTMLDivElement>(null)
  const socketRef = useRef<Socket | null>(null)
  const visitorIdRef = useRef<string>('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  // 1. Load tenants
  useEffect(() => {
    let cancelled = false
    const loadTenants = async () => {
      try {
        const res = await traceService.getTenants()
        if (cancelled) return
        const list = res.data || []
        setTenants(list)
        if (!tenantId && list.length > 0) {
          const selected = list[0].id
          setTenantId(selected)
          setSearchParams({ tenantId: selected }, { replace: true })
        }
      } catch (err) {
        console.error('Error cargando tenants:', err)
      }
    }
    void loadTenants()
    return () => {
      cancelled = true
    }
  }, [])

  // 2. When tenant changes, init visitor and session
  useEffect(() => {
    if (!tenantId) return

    visitorIdRef.current = getOrCreateVisitorId(tenantId)
    const activeTenant = tenants.find((t) => t.id === tenantId)
    if (activeTenant) {
      setBotName(activeTenant.name || 'Asistente de Ventas')
    }

    setMessages([
      {
        role: 'bot',
        text: `¡Hola! 👋 Bienvenido a la tienda. ¿En qué repuesto o producto podemos ayudarte hoy?`,
        time: getTime(),
        stage: 'DISCOVERY',
      },
    ])
    setTurnTraces([])
    setConversationId('')
    setCurrentStage('DISCOVERY')
    setStageHistory(['DISCOVERY'])
    setSelectedProduct(null)
    setEvaluationResult(null)

    // Setup WebChat session & socket connection
    let isMounted = true
    setSocketStatus('connecting')

    traceService
      .getTestChatSession({ tenantId, visitorId: visitorIdRef.current })
      .then((session) => {
        if (!isMounted) return
        if (session.botName) setBotName(session.botName)

        const socketUrl = getApiBaseUrl()
        const socket = createWebchatSocket(socketUrl, {
          widgetToken: session.widgetToken,
          tenantSlug: session.tenantSlug || tenantId,
          visitorId: session.visitorId,
        })

        socket.on('connect', () => {
          if (isMounted) setSocketStatus('connected')
        })

        socket.on('disconnect', () => {
          if (isMounted) setSocketStatus('offline')
        })

        socket.on('connect_error', () => {
          if (isMounted) setSocketStatus('offline')
        })

        socket.on('webchat:message', (payload: any) => {
          if (!isMounted) return
          if (payload?.from === 'agent' && (payload?.text || payload?.mediaUrl)) {
            setBotTyping(false)
            setMessages((prev) => {
              const last = prev[prev.length - 1]
              if (last && last.role === 'bot' && last.text === payload.text && last.mediaUrl === payload.mediaUrl) {
                return prev
              }
              return [
                ...prev,
                {
                  id: payload.id,
                  role: 'bot',
                  text: payload.text || '',
                  mediaUrl: payload.mediaUrl,
                  mediaMimetype: payload.mimetype,
                  time: getTime(),
                },
              ]
            })
          }
        })

        socket.on('webchat:typing', (payload: any) => {
          if (!isMounted) return
          setBotTyping(Boolean(payload?.isTyping))
        })

        socketRef.current = socket
      })
      .catch((err) => {
        console.warn('Socket connection fallback to HTTP:', err)
        if (isMounted) setSocketStatus('offline')
      })

    return () => {
      isMounted = false
      if (socketRef.current) {
        socketRef.current.disconnect()
        socketRef.current = null
      }
    }
  }, [tenantId, tenants])

  // Scroll to bottom on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, botTyping])

  const handleSelectTenant = (newTenantId: string) => {
    setTenantId(newTenantId)
    setSearchParams({ tenantId: newTenantId }, { replace: true })
  }

  const handleResetChat = async () => {
    if (!tenantId || isResetting) return
    setIsResetting(true)
    try {
      await traceService.resetTestChatSession({
        tenantId,
        visitorId: visitorIdRef.current,
      })
      toast.success('Sesión del simulador reiniciada')
      setMessages([
        {
          role: 'bot',
          text: `¡Hola! 👋 Bienvenido a la tienda. ¿En qué repuesto o producto podemos ayudarte hoy?`,
          time: getTime(),
          stage: 'DISCOVERY',
        },
      ])
      setTurnTraces([])
      setConversationId('')
      setCurrentStage('DISCOVERY')
      setStageHistory(['DISCOVERY'])
      setSelectedProduct(null)
      setEvaluationResult(null)
    } catch {
      toast.error('No se pudo reiniciar la sesión de prueba')
    } finally {
      setIsResetting(false)
    }
  }

  const handleReplayConversation = async (sourceId?: string) => {
    const targetSourceId = (sourceId || replayConversationId).trim()
    if (!tenantId || !targetSourceId || isReplaying) return
    setIsReplaying(true)
    const toastId = toast.loading(`Reproduciendo conversación ${targetSourceId.slice(0, 8)}...`)

    try {
      const result = await traceService.replayTestConversation({
        tenantId,
        sourceConversationId: targetSourceId,
        evaluate: true,
      })

      if (!result.ok) {
        throw new Error('El replay no devolvió un resultado exitoso')
      }

      setConversationId(result.conversationId)

      const replayedMessages: ChatMessage[] = []
      const replayedTraces: TurnTraceInfo[] = []
      let lastStage = 'DISCOVERY'
      let lastProduct: any = null

      result.turns.forEach((turn) => {
        replayedMessages.push({
          role: 'user',
          text: turn.inboundText,
          mediaUrl: turn.mediaUrl ?? undefined,
          time: getTime(),
        })

        if (turn.reply || turn.mediaUrl) {
          replayedMessages.push({
            role: 'bot',
            text: turn.reply || '',
            mediaUrl: turn.mediaUrl ?? undefined,
            stage: turn.stage || undefined,
            time: getTime(),
          })
        }

        if (turn.stage) lastStage = turn.stage
        if (turn.selectedProduct) lastProduct = turn.selectedProduct

        replayedTraces.push({
          turnIndex: turn.turnIndex,
          userInput: turn.inboundText,
          botReply: turn.reply,
          stage: turn.stage || undefined,
          intent: turn.intent || undefined,
          timestamp: new Date().toISOString(),
          selectedProduct: turn.selectedProduct,
          metadata: {
            traceId: turn.traceId,
            supervisorReflexionExecuted: turn.supervisorReflexionExecuted,
            traceStepsCount: turn.traceSteps?.length ?? 0,
          },
        })
      })

      setMessages(replayedMessages)
      setTurnTraces(replayedTraces)
      setCurrentStage(lastStage)
      setSelectedProduct(lastProduct)

      if (result.evaluation) {
        setEvaluationResult(result.evaluation)
      }

      toast.success(`Replay completado: ${result.totalTurns} turnos analizados`, { id: toastId })
    } catch (err: any) {
      toast.error(err.message || 'Error al reproducir conversación', { id: toastId })
    } finally {
      setIsReplaying(false)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imágenes (JPG, PNG, WebP)')
      return
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('La imagen no debe superar los 8MB')
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setPendingImage({
          dataUrl: reader.result,
          name: file.name,
          file,
        })
      }
    }
    reader.readAsDataURL(file)
    e.target.value = ''
  }

  const handleSendMessage = async (textOverride?: string) => {
    const text = (textOverride !== undefined ? textOverride : userInput).trim()
    if (!text && !pendingImage) return

    const imageToSend = pendingImage
    setPendingImage(null)
    if (textOverride === undefined) {
      setUserInput('')
    }

    const startTurnTime = Date.now()
    const userMsg: ChatMessage = {
      role: 'user',
      text,
      mediaUrl: imageToSend?.dataUrl,
      mediaMimetype: imageToSend?.file.type || 'image/jpeg',
      time: getTime(),
    }

    setMessages((prev) => [...prev, userMsg])
    setBotTyping(true)

    try {
      const result: SendTestMessageResult = await traceService.sendTestChatMessage({
        tenantId,
        visitorId: visitorIdRef.current,
        text,
        visitorName: 'Inspector Admin',
        mediaUrl: imageToSend?.dataUrl,
        mediaMimetype: imageToSend?.file.type || 'image/jpeg',
      })

      const elapsedMs = Date.now() - startTurnTime
      setBotTyping(false)

      if (result.conversationId) {
        setConversationId(result.conversationId)
      }

      const effectiveMedia = result.mediaUrl || (result.selectedProduct as any)?.imageUrl
      if (result.reply || effectiveMedia) {
        setMessages((prev) => {
          const last = prev[prev.length - 1]
          if (last && last.role === 'bot' && last.text === result.reply && last.mediaUrl === effectiveMedia) {
            return prev
          }
          return [
            ...prev,
            {
              id: result.conversationId,
              role: 'bot',
              text: result.reply || '',
              mediaUrl: effectiveMedia,
              time: getTime(),
              stage: result.stage,
            },
          ]
        })
      }

      if (result.stage) {
        setCurrentStage(result.stage)
        setStageHistory((prev) => [...prev, result.stage!])
      }

      if (result.selectedProduct) {
        setSelectedProduct(result.selectedProduct)
      } else {
        setSelectedProduct(null)
      }

      // Record turn trace
      const newTurnTrace: TurnTraceInfo = {
        turnIndex: turnTraces.length + 1,
        userInput: text,
        botReply: result.reply || '',
        stage: result.stage,
        intent: result.intent,
        timestamp: new Date().toLocaleTimeString(),
        durationMs: elapsedMs,
        selectedProduct: result.selectedProduct,
        metadata: (result as any).metadata,
      }
      setTurnTraces((prev) => [newTurnTrace, ...prev])
    } catch {
      setBotTyping(false)
      toast.error('Error al procesar el mensaje en el simulador')
      setMessages((prev) => [
        ...prev,
        {
          role: 'bot',
          text: '⚠️ Disculpa, hubo un problema al procesar el turno en el agente de ventas.',
          time: getTime(),
        },
      ])
    }
  }

  const handleEvaluateChat = async () => {
    if (!tenantId || isEvaluating) return
    setIsEvaluating(true)
    try {
      const payloadMessages = messages.map((m) => ({
        role: (m.role === 'bot' ? 'assistant' : 'user') as 'user' | 'assistant' | 'system',
        content: m.text,
        timestamp: m.time,
      }))

      const evaluation = await traceService.evaluateTestChat({
        tenantId,
        visitorId: visitorIdRef.current,
        conversationId,
        messages: payloadMessages,
        stageHistory,
        finalStage: currentStage,
        selectedProduct,
        deepEvaluation: true,
      })

      setEvaluationResult(evaluation)
      setIsModalOpen(true)
      toast.success(`Evaluación completada: ${evaluation.overallScore}/100`)
    } catch {
      toast.error('Error al evaluar la conversación de prueba')
    } finally {
      setIsEvaluating(false)
    }
  }

  const stageBadge = getStageBadge(currentStage)

  return (
    <div style={{ fontFamily: "'IBM Plex Mono', monospace", background: '#0a0a0f', minHeight: '100vh', color: '#e2e8f0', display: 'flex', flexDirection: 'column' }}>
      {/* Top Header Bar */}
      <div style={{ padding: '14px 24px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(255,255,255,0.02)', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Link
            to={conversationId ? `/admin/debug?tenantId=${tenantId}&conversationId=${conversationId}` : `/admin/debug?tenantId=${tenantId}`}
            style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, color: '#cbd5e1', textDecoration: 'none', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            ← TRACE INSPECTOR
          </Link>
          <Link
            to={`/admin/debug/observability?tenantId=${tenantId}`}
            style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, color: '#86efac', textDecoration: 'none', background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.28)' }}
          >
            OBSERVABILIDAD
          </Link>
          <span style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, color: '#f472b6', background: 'rgba(236,72,153,0.18)', border: '1px solid rgba(236,72,153,0.35)' }}>
            SIMULADOR WHATSAPP
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
            <Activity style={{ width: 14, height: 14, color: '#22c55e' }} />
            <span>Interactive Funnel & Trace Environment</span>
          </div>
        </div>

        {/* Tenant selector & Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>TENANT:</span>
            <select
              value={tenantId}
              onChange={(e) => handleSelectTenant(e.target.value)}
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.14)', color: '#e2e8f0', borderRadius: 8, padding: '6px 10px', fontSize: 12, minWidth: 260 }}
            >
              {tenants.map((t) => (
                <option key={t.id} value={t.id} style={{ background: '#0a0a0f' }}>
                  {t.name || t.slug || t.id} · {t.id.slice(0, 8)}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={handleResetChat}
            disabled={isResetting}
            title="Reiniciar chat y limpiar memoria del visitante"
            style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.1)', cursor: isResetting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <RotateCcw style={{ width: 14, height: 14 }} className={isResetting ? 'animate-spin' : ''} />
            RESET
          </button>

          <button
            onClick={handleEvaluateChat}
            disabled={isEvaluating}
            style={{
              padding: '6px 14px',
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 700,
              background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.25), rgba(236, 72, 153, 0.25))',
              color: '#f472b6',
              border: '1px solid rgba(236, 72, 153, 0.4)',
              cursor: isEvaluating ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Bot style={{ width: 14, height: 14 }} />
            {isEvaluating ? 'AUDITANDO...' : 'AUDITAR CON IA'}
          </button>

          {evaluationResult && (
            <div
              onClick={() => setIsModalOpen(true)}
              style={{
                padding: '4px 10px',
                borderRadius: 8,
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
                background: evaluationResult.overallScore >= 80 ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)',
                color: evaluationResult.overallScore >= 80 ? '#4ade80' : '#fbbf24',
                border: `1px solid ${evaluationResult.overallScore >= 80 ? 'rgba(34,197,94,0.35)' : 'rgba(245,158,11,0.35)'}`,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
              title="Click para ver reporte de auditoría completo"
            >
              <span>Score: {evaluationResult.overallScore}/100</span>
              <span>📋</span>
            </div>
          )}
        </div>
      </div>

      {/* Replay E2E Banner if replayConversationId is present in URL */}
      {replayConversationId && (
        <div style={{ padding: '8px 24px', background: 'rgba(236,72,153,0.1)', borderBottom: '1px solid rgba(236,72,153,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#f472b6' }}>REPLAY E2E ACTIVO:</span>
            <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#fbcfe8', background: 'rgba(236,72,153,0.2)', padding: '2px 6px', borderRadius: 4 }}>
              {replayConversationId}
            </span>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>
              Reproduce el historial exacto contra el runtime actual y el Supervisor de Reflexión.
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={() => handleReplayConversation()}
              disabled={isReplaying}
              style={{
                padding: '4px 12px',
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 700,
                background: '#ec4899',
                color: '#fff',
                border: 'none',
                cursor: isReplaying ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                opacity: isReplaying ? 0.7 : 1,
              }}
            >
              <Play style={{ width: 12, height: 12 }} />
              {isReplaying ? 'REPRODUCIENDO...' : 'EJECUTAR REPLAY'}
            </button>
            <button
              onClick={() => {
                const nextParams = new URLSearchParams(searchParams)
                nextParams.delete('replayConversationId')
                setSearchParams(nextParams, { replace: true })
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'rgba(255,255,255,0.4)',
                cursor: 'pointer',
                padding: 4,
                display: 'flex',
                alignItems: 'center',
              }}
              title="Cerrar banner de replay"
            >
              <X style={{ width: 14, height: 14 }} />
            </button>
          </div>
        </div>
      )}

      {/* Quick Scenarios Bar (Tienda de Repuestos / Counter Dynamics) */}
      <div style={{ padding: '8px 24px', background: 'rgba(255,255,255,0.015)', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', gap: 8, overflowX: 'auto', whiteSpace: 'nowrap' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.35)', flexShrink: 0 }}>ESCENARIOS DE PRUEBA:</span>
        {QUICK_SCENARIOS.map((sc) => (
          <button
            key={sc.label}
            onClick={() => handleSendMessage(sc.text)}
            disabled={botTyping}
            style={{
              padding: '4px 10px',
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 600,
              background: 'rgba(255,255,255,0.04)',
              color: '#93c5fd',
              border: '1px solid rgba(255,255,255,0.08)',
              cursor: botTyping ? 'not-allowed' : 'pointer',
              opacity: botTyping ? 0.5 : 1,
              flexShrink: 0,
            }}
          >
            {sc.label}
          </button>
        ))}
      </div>

      {/* Main Split Screen Container */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left Column: WhatsApp Phone Simulator (430px) */}
        <div style={{ width: 440, borderRight: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', background: '#0f1117', flexShrink: 0, position: 'relative' }}>
          {/* Diagnostic Sub-header */}
          <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.02)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: 'rgba(255,255,255,0.4)' }}>Embudo:</span>
              <span className={`px-2 py-0.5 rounded text-[10px] border flex items-center gap-1 ${stageBadge.color}`}>
                <span>{stageBadge.icon}</span>
                <span>{stageBadge.label}</span>
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: socketStatus === 'connected' ? '#22c55e' : socketStatus === 'connecting' ? '#f59e0b' : '#64748b' }} />
              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>
                {socketStatus === 'connected' ? 'Socket En vivo' : socketStatus === 'connecting' ? 'Conectando...' : 'HTTP Fallback'}
              </span>
            </div>
          </div>

          {/* Confirmed Product Banner */}
          {selectedProduct && (
            <div style={{ padding: '8px 16px', background: 'rgba(16,185,129,0.1)', borderBottom: '1px solid rgba(16,185,129,0.25)', display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#6ee7b7' }}>
              <CheckCircle2 style={{ width: 14, height: 14, color: '#10b981', flexShrink: 0 }} />
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                Producto Confirmado: <strong>{selectedProduct.name}</strong> {selectedProduct.price ? `($${selectedProduct.price})` : ''}
              </span>
            </div>
          )}

          {/* WhatsApp Header */}
          <div style={{ background: '#075E54', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10, color: '#fff', borderBottom: '1px solid rgba(0,0,0,0.15)' }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#128C7E', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, color: '#fff' }}>
              🤖
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {botName}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>
                {botTyping ? 'escribiendo...' : 'en línea (Asistente LangGraph)'}
              </div>
            </div>
          </div>

          {/* WhatsApp Chat Message Scroll Area */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px 14px',
              background: '#0d1418',
              backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)',
              backgroundSize: '16px 16px',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {/* Today Pill */}
            <div style={{ textAlign: 'center', margin: '4px 0' }}>
              <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 6, background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.45)' }}>
                HOY
              </span>
            </div>

            {messages.map((m, idx) => {
              const isUser = m.role === 'user'
              const msgKey = m.id ?? `sim-msg-${m.role}-${idx}-${m.time}`
              return (
                <div
                  key={msgKey}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isUser ? 'flex-end' : 'flex-start',
                    width: '100%',
                  }}
                >
                  <div
                    style={{
                      maxWidth: '85%',
                      padding: '8px 12px',
                      borderRadius: isUser ? '10px 10px 2px 10px' : '10px 10px 10px 2px',
                      background: isUser ? '#005c4b' : '#202c33',
                      color: '#e9edef',
                      fontSize: 12.5,
                      lineHeight: 1.45,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                      wordBreak: 'break-word',
                    }}
                  >
                    {m.mediaUrl && (
                      <button
                        type="button"
                        aria-label="Ver imagen ampliada"
                        style={{ display: 'block', padding: 0, border: 'none', background: 'none', marginBottom: 6, borderRadius: 6, overflow: 'hidden', cursor: 'pointer', width: '100%' }}
                        onClick={() => setZoomedImage(m.mediaUrl || null)}
                      >
                        <img src={m.mediaUrl} alt="Adjunto" style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'cover', display: 'block' }} />
                      </button>
                    )}
                    <div>{m.text}</div>
                    <div style={{ fontSize: 9, textAlign: 'right', marginTop: 4, color: 'rgba(255,255,255,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                      <span>{m.time}</span>
                      {isUser && <span style={{ color: '#53bdeb' }}>✓✓</span>}
                    </div>
                  </div>
                </div>
              )
            })}

            {botTyping && (
              <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                <div style={{ padding: '8px 14px', borderRadius: '10px 10px 10px 2px', background: '#202c33', color: '#8696a0', fontSize: 11, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>Escribiendo respuesta</span>
                  <span className="animate-pulse">●●●</span>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Pending Image Attachment Chip */}
          {pendingImage && (
            <div style={{ padding: '6px 14px', background: 'rgba(14,165,233,0.1)', borderTop: '1px solid rgba(14,165,233,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, color: '#7dd3fc' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <ImageIcon style={{ width: 14, height: 14 }} />
                <span>{pendingImage.name}</span>
              </div>
              <button onClick={() => setPendingImage(null)} style={{ background: 'transparent', border: 'none', color: '#f87171', cursor: 'pointer' }}>
                <X style={{ width: 14, height: 14 }} />
              </button>
            </div>
          )}

          {/* Chat Input Bar */}
          <div style={{ padding: '10px 12px', background: '#202c33', display: 'flex', alignItems: 'center', gap: 8, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              style={{ display: 'none' }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              title="Adjuntar imagen de repuesto"
              style={{ background: 'transparent', border: 'none', color: '#8696a0', cursor: 'pointer', padding: 4 }}
            >
              <ImageIcon style={{ width: 20, height: 20 }} />
            </button>

            <input
              type="text"
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSendMessage()
                }
              }}
              placeholder="Escribe un mensaje de prueba..."
              disabled={botTyping}
              style={{ flex: 1, background: '#2a3942', border: 'none', borderRadius: 8, padding: '8px 12px', color: '#d1d7db', fontSize: 12, outline: 'none' }}
            />

            <button
              onClick={() => handleSendMessage()}
              disabled={(!userInput.trim() && !pendingImage) || botTyping}
              style={{
                background: '#00a884',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: 34,
                height: 34,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: (!userInput.trim() && !pendingImage) || botTyping ? 'not-allowed' : 'pointer',
                opacity: (!userInput.trim() && !pendingImage) || botTyping ? 0.4 : 1,
              }}
            >
              <Send style={{ width: 16, height: 16 }} />
            </button>
          </div>
        </div>

        {/* Right Column: Live LangGraph Trace Stream & State Inspector */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#0a0a0f' }}>
          {/* Sub-header with Tabs & Deep Link */}
          <div style={{ padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(255,255,255,0.015)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                onClick={() => setActiveTab('turns')}
                style={{
                  padding: '6px 14px',
                  borderRadius: 7,
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                  background: activeTab === 'turns' ? 'rgba(99,102,241,0.2)' : 'transparent',
                  color: activeTab === 'turns' ? '#a78bfa' : 'rgba(255,255,255,0.4)',
                  border: activeTab === 'turns' ? '1px solid rgba(99,102,241,0.4)' : '1px solid transparent',
                }}
              >
                LIVE TURNS STREAM ({turnTraces.length})
              </button>
              <button
                onClick={() => setActiveTab('state')}
                style={{
                  padding: '6px 14px',
                  borderRadius: 7,
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                  background: activeTab === 'state' ? 'rgba(99,102,241,0.2)' : 'transparent',
                  color: activeTab === 'state' ? '#a78bfa' : 'rgba(255,255,255,0.4)',
                  border: activeTab === 'state' ? '1px solid rgba(99,102,241,0.4)' : '1px solid transparent',
                }}
              >
                FUNNEL STATE & SNAPSHOT
              </button>
            </div>

            {conversationId && (
              <Link
                to={`/admin/debug?tenantId=${tenantId}&conversationId=${conversationId}`}
                style={{
                  padding: '5px 12px',
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 700,
                  background: 'rgba(14,165,233,0.12)',
                  color: '#7dd3fc',
                  border: '1px solid rgba(14,165,233,0.25)',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span>Abrir en Trace Inspector</span>
                <ExternalLink style={{ width: 12, height: 12 }} />
              </Link>
            )}
          </div>

          {/* Inspector Content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
            {activeTab === 'turns' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {turnTraces.length === 0 ? (
                  <div style={{ padding: 30, textAlign: 'center', color: 'rgba(255,255,255,0.3)', border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 12 }}>
                    <Bot style={{ width: 32, height: 32, margin: '0 auto 10px', opacity: 0.4 }} />
                    <p style={{ margin: 0, fontSize: 13 }}>No hay turnos registrados aún en esta sesión de prueba.</p>
                    <p style={{ margin: '6px 0 0', fontSize: 11, color: 'rgba(255,255,255,0.25)' }}>
                      Escribe un mensaje en el simulador o selecciona uno de los escenarios rápidos para ver la traza en tiempo real.
                    </p>
                  </div>
                ) : (
                  turnTraces.map((turn) => (
                    <div
                      key={turn.turnIndex}
                      style={{
                        padding: 16,
                        borderRadius: 12,
                        background: 'rgba(255,255,255,0.02)',
                        border: '1px solid rgba(255,255,255,0.07)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10,
                      }}
                    >
                      {/* Turn Header */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#a78bfa' }}>TURNO #{turn.turnIndex}</span>
                          <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)' }}>
                            {turn.durationMs}ms
                          </span>
                          {turn.stage && (
                            <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'rgba(99,102,241,0.15)', color: '#818cf8' }}>
                              stage={turn.stage}
                            </span>
                          )}
                          {turn.intent && (
                            <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'rgba(16,185,129,0.15)', color: '#34d399' }}>
                              intent={turn.intent}
                            </span>
                          )}
                          {turn.metadata?.supervisorReflexionExecuted && (
                            <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'rgba(236,72,153,0.2)', color: '#f472b6', border: '1px solid rgba(236,72,153,0.4)', fontWeight: 700 }}>
                              SUPERVISOR REFLEXION
                            </span>
                          )}
                        </div>
                        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>{turn.timestamp}</span>
                      </div>

                      {/* Turn Input/Output */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 12 }}>
                        <div style={{ padding: 10, borderRadius: 8, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                          <div style={{ fontSize: 10, color: '#38bdf8', marginBottom: 4, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <User style={{ width: 12, height: 12 }} /> USUARIO
                          </div>
                          <div style={{ color: '#e2e8f0', whiteSpace: 'pre-wrap' }}>{turn.userInput}</div>
                        </div>

                        <div style={{ padding: 10, borderRadius: 8, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                          <div style={{ fontSize: 10, color: '#4ade80', marginBottom: 4, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Bot style={{ width: 12, height: 12 }} /> AGENTE (RESPUESTA FINAL)
                          </div>
                          <div style={{ color: '#e2e8f0', whiteSpace: 'pre-wrap' }}>{turn.botReply}</div>
                        </div>
                      </div>

                      {/* Selected Product Snapshot if present */}
                      {turn.selectedProduct && (
                        <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', fontSize: 11, color: '#6ee7b7', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <ShoppingBag style={{ width: 14, height: 14 }} />
                          <span>
                            Producto en contexto: <strong>{turn.selectedProduct.name}</strong> (${turn.selectedProduct.price})
                          </span>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'state' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ padding: 16, borderRadius: 12, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Layers style={{ width: 16, height: 16, color: '#818cf8' }} />
                    ESTADO DEL EMBUDO (SINGLE AUTHORITATIVE SOURCE)
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, fontSize: 12 }}>
                    <div style={{ padding: 10, borderRadius: 8, background: 'rgba(255,255,255,0.02)' }}>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 2 }}>ETAPA ACTUAL</div>
                      <div style={{ fontWeight: 700, color: '#a78bfa' }}>{currentStage}</div>
                    </div>
                    <div style={{ padding: 10, borderRadius: 8, background: 'rgba(255,255,255,0.02)' }}>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 2 }}>HISTORIAL ETAPAS</div>
                      <div style={{ fontWeight: 700, color: '#38bdf8' }}>{stageHistory.join(' → ')}</div>
                    </div>
                    <div style={{ padding: 10, borderRadius: 8, background: 'rgba(255,255,255,0.02)' }}>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 2 }}>TOTAL TURNOS</div>
                      <div style={{ fontWeight: 700, color: '#34d399' }}>{turnTraces.length}</div>
                    </div>
                  </div>
                </div>

                <div style={{ padding: 16, borderRadius: 12, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ShieldCheck style={{ width: 16, height: 16, color: '#10b981' }} />
                    GUARDARRAÍLES Y SEGURIDAD COMERCIAL
                  </div>
                  <div style={{ fontSize: 12, lineHeight: 1.6, color: 'rgba(255,255,255,0.7)' }}>
                    <div>• <strong>checkout_without_product = 0:</strong> {selectedProduct ? '✅ Producto asignado' : '🛡️ Protegido (Sin producto seleccionado)'}</div>
                    <div>• <strong>Tenant isolation:</strong> ✅ Sesión verificada exclusivamente para {tenantId}</div>
                    <div>• <strong>Escalación humana:</strong> Al confirmar compra, la conversación cambia automáticamente a modo humano.</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AI Evaluation Modal */}
      <TestChatEvaluationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        evaluation={evaluationResult}
        isLoading={isEvaluating}
      />

      {/* Zoomed Image Modal */}
      {zoomedImage && (
        <div
          onClick={() => setZoomedImage(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}
        >
          <img src={zoomedImage} alt="Zoom" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8 }} />
        </div>
      )}
    </div>
  )
}
